import { Opportunity, type Association, type Team } from "@entities";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import {
  DatePicker,
  DateTimePicker,
  LocalizationProvider,
  TimePicker,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { heIL } from "@mui/x-date-pickers/locales";
import { associationRepo } from "@repositories";
import {
  OPPORTUNITY_TITLE_MAX_LENGTH,
  TIME_DESCRIPTION_MAX_LENGTH,
  VOLUNTEER_DESCRIPTION_MAX_LENGTH,
} from "@shared/helpers/constants";
import {
  applyPickedCalendarDate,
  buildGroupChildBulkUpdateFields,
  bulkRelationSensitiveWarning,
  formatBulkContentValue,
  formatBulkLastCallSummary,
  formatBulkLocationSummary,
  formatBulkScheduleSummary,
  selectGroupChildBulkUpdateWrite,
  validateGroupChildBulkUpdatePatch,
  type GroupChildBulkUpdatePatchInput,
  type GroupChildBulkWrite,
} from "@shared/helpers/group-instance-bulk-update";
import { GROUP_INSTANCE_BULK_MAX } from "@shared/helpers/group-instance-schedule";
import { MATCH_WEIGHT_MAX, MATCH_WEIGHT_MIN } from "@shared/helpers/match-weight";
import {
  OPPORTUNITY_VOLUNTEER_MODE_HEB,
  OpportunityVolunteerMode,
  type OpportunityVolunteerModeType,
} from "@shared/helpers/opportunity-volunteer-mode";
import {
  VolunteerRegistrationOutcome,
  type VolunteerRegistrationOutcomeType,
} from "@shared/helpers/volunteer-registration-outcome";
import {
  ADMIN_BULK_EDIT_SENSITIVE_HINT,
  ADMIN_BULK_EDIT_SENSITIVE_TITLE,
  ADMIN_GROUP_BULK_EDIT_APPLY_LAST_CALL,
  ADMIN_GROUP_BULK_EDIT_APPLY_LOCATION,
  ADMIN_GROUP_BULK_EDIT_APPLY_SCHEDULE,
  ADMIN_GROUP_BULK_EDIT_APPLIES_TO,
  ADMIN_GROUP_BULK_EDIT_BACK,
  ADMIN_GROUP_BULK_EDIT_CONFIRM,
  ADMIN_GROUP_BULK_EDIT_CONTENT_TITLE,
  ADMIN_GROUP_BULK_EDIT_CRITICAL_TITLE,
  ADMIN_GROUP_BULK_EDIT_ERROR,
  ADMIN_GROUP_BULK_EDIT_HINT,
  ADMIN_GROUP_BULK_EDIT_LAST_CALL_UNCHANGED,
  ADMIN_GROUP_BULK_EDIT_LOCATION_UNCHANGED,
  ADMIN_GROUP_BULK_EDIT_MAX,
  ADMIN_GROUP_BULK_EDIT_NOTHING,
  ADMIN_GROUP_BULK_EDIT_PREVIEW,
  ADMIN_GROUP_BULK_EDIT_SCHEDULE_UNCHANGED,
  ADMIN_GROUP_BULK_EDIT_TIME_WARNING,
  ADMIN_GROUP_BULK_EDIT_TITLE,
  ADMIN_GROUP_BULK_EDIT_UNCHANGED,
} from "@shared/helpers/opportunity-group-admin-copy";
import { compareBySortOrder } from "@shared/helpers/category-sort";
import dayjs, { type Dayjs } from "dayjs";
import { isValid } from "date-fns";
import { type FC, useEffect, useMemo, useState } from "react";
import { useAlert } from "../../contexts/AlertProvider";
import { useOrganization } from "../../contexts/OrganizationProvider";
import { REQUEST_DEFAULT_VALUES } from "../../contexts/RequestProvider";
import EventImageSection from "../event-image-section/EventImageSection";
import { FormGoogle } from "../form-components/form-google-autocomplete/FormGoogle";
import AidRelatedCard from "../popups/aid-related-card/AidRelatedCard";
import RequesterDetails from "../requester-details/RequesterDetails";
import { TeamsSelectDialog } from "../teams-select";
import { TwoCheckbox } from "../two-checkbox/TwoCheckbox";

type DateEvent = (Dayjs & { $d?: Date }) | null;

type ManagerUrgency = "STANDARD" | "HIGH" | "CRITICAL";

type BulkApply = {
  title: boolean;
  description: boolean;
  image: boolean;
  amount: boolean;
  requester: boolean;
  comments: boolean;
  age: boolean;
  requiredGender: boolean;
  requiresVehicle: boolean;
  isSensitive: boolean;
  volunteerMode: boolean;
  isLongTerm: boolean;
  isHidden: boolean;
  teams: boolean;
  categories: boolean;
  populations: boolean;
  registrationFlow: boolean;
  matchWeight: boolean;
  dailyTarget: boolean;
  managerUrgency: boolean;
  callCenterUrgency: boolean;
  callCenterDifficulty: boolean;
  whatsapp: boolean;
  schedule: boolean;
  location: boolean;
  lastCall: boolean;
};

const APPLY_OFF: BulkApply = {
  title: false,
  description: false,
  image: false,
  amount: false,
  requester: false,
  comments: false,
  age: false,
  requiredGender: false,
  requiresVehicle: false,
  isSensitive: false,
  volunteerMode: false,
  isLongTerm: false,
  isHidden: false,
  teams: false,
  categories: false,
  populations: false,
  registrationFlow: false,
  matchWeight: false,
  dailyTarget: false,
  managerUrgency: false,
  callCenterUrgency: false,
  callCenterDifficulty: false,
  whatsapp: false,
  schedule: false,
  location: false,
  lastCall: false,
};

type BulkEditDraft = {
  title: string;
  description: string;
  imageId: string;
  associationRequestedId: string | null;
  associationRequested?: Association;
  hasTime: boolean;
  hasStartTime: boolean;
  startDate: Date | null;
  endDate: Date | null;
  timeDescription: string;
  location: string;
  coordinates: { lat: number; lng: number };
  isRemote: boolean;
  amount: number;
  lastCallToVolunteers: Date | null;
  comments: string;
  minAge: number;
  maxAge: number;
  requiredGender: "MALE" | "FEMALE" | null;
  requiresVehicle: boolean;
  isSensitive: boolean;
  volunteerMode: OpportunityVolunteerModeType;
  isLongTerm: boolean;
  isHidden: boolean;
  teams: Team[];
  categoryIds: string[];
  populationIds: string[];
  volunteerRegistrationFlow: VolunteerRegistrationOutcomeType;
  defaultMatchWeight: number;
  dailyVolunteerTarget: string;
  managerUrgency: ManagerUrgency;
  callCenterUrgency: number;
  callCenterDifficulty: number;
  whatsappGroupUrl: string;
};

function draftFromOpportunity(source: Opportunity): BulkEditDraft {
  return {
    title: source.title ?? "",
    description: source.description ?? "",
    imageId: source.imageId ?? "",
    associationRequestedId: source.associationRequestedId ?? null,
    associationRequested: source.associationRequested,
    hasTime: source.hasTime !== false,
    hasStartTime: source.hasStartTime !== false,
    startDate: source.startDate ? new Date(source.startDate) : null,
    endDate: source.endDate ? new Date(source.endDate) : null,
    timeDescription: source.timeDescription ?? "",
    location: source.location ?? "",
    coordinates: source.coordinates ?? REQUEST_DEFAULT_VALUES.coordinates,
    isRemote: !!source.isRemote,
    amount: Math.max(1, source.amount ?? 1),
    lastCallToVolunteers: source.lastCallToVolunteers
      ? new Date(source.lastCallToVolunteers)
      : null,
    comments: source.comments ?? "",
    minAge: source.minAge ?? 16,
    maxAge: source.maxAge ?? 100,
    requiredGender:
      source.requiredGender === "MALE" || source.requiredGender === "FEMALE"
        ? source.requiredGender
        : null,
    requiresVehicle: !!source.requiresVehicle,
    isSensitive: !!source.isSensitive,
    volunteerMode:
      source.volunteerMode ?? OpportunityVolunteerMode.INDIVIDUAL,
    isLongTerm: !!source.isLongTerm,
    isHidden: !!source.isHidden,
    teams: (source.teams ?? []).flatMap((row) => {
      if (row.team) return [row.team];
      if (!row.teamId) return [];
      return [{ id: row.teamId, name: row.teamId } as Team];
    }),
    categoryIds: (source.categories ?? [])
      .map((row) => row.categoryId)
      .filter(Boolean),
    populationIds: (source.populations ?? [])
      .map((row) => row.populationId)
      .filter(Boolean),
    volunteerRegistrationFlow:
      source.volunteerRegistrationFlow ?? VolunteerRegistrationOutcome.SMS_DETAILS,
    defaultMatchWeight: source.defaultMatchWeight ?? 1,
    dailyVolunteerTarget:
      source.dailyVolunteerTarget != null
        ? String(source.dailyVolunteerTarget)
        : "",
    managerUrgency: source.managerUrgency ?? "STANDARD",
    callCenterUrgency: source.callCenterUrgency ?? 0,
    callCenterDifficulty: source.callCenterDifficulty ?? 3,
    whatsappGroupUrl: source.whatsappGroupUrl ?? "",
  };
}

function patchFromDraft(
  draft: BulkEditDraft,
  apply: BulkApply,
): GroupChildBulkUpdatePatchInput {
  const dailyRaw = draft.dailyVolunteerTarget.trim();
  const dailyVolunteerTarget = dailyRaw ? Number(dailyRaw) : null;
  return {
    title: draft.title,
    description: draft.description,
    imageId: draft.imageId,
    associationRequestedId: draft.associationRequestedId,
    location: draft.isRemote ? "מרחוק" : draft.location.trim(),
    coordinates: draft.isRemote
      ? REQUEST_DEFAULT_VALUES.coordinates
      : draft.coordinates,
    isRemote: draft.isRemote,
    hasTime: draft.hasTime,
    hasStartTime: draft.hasTime ? draft.hasStartTime : true,
    startDate: draft.hasTime && draft.hasStartTime ? draft.startDate : null,
    endDate: draft.hasTime ? draft.endDate : null,
    timeDescription: draft.hasTime ? "" : draft.timeDescription.trim(),
    amount: draft.amount,
    applySchedule: apply.schedule,
    applyLocation: apply.location,
    applyLastCall: apply.lastCall,
    lastCallToVolunteers: draft.lastCallToVolunteers,
    applyTitle: apply.title,
    applyDescription: apply.description,
    applyImage: apply.image,
    applyAmount: apply.amount,
    applyRequester: apply.requester,
    applyComments: apply.comments,
    comments: draft.comments,
    applyAge: apply.age,
    minAge: draft.minAge,
    maxAge: draft.maxAge,
    applyRequiredGender: apply.requiredGender,
    requiredGender: draft.requiredGender,
    applyRequiresVehicle: apply.requiresVehicle,
    requiresVehicle: draft.requiresVehicle,
    applyIsSensitive: apply.isSensitive,
    isSensitive: draft.isSensitive,
    applyVolunteerMode: apply.volunteerMode,
    volunteerMode: draft.volunteerMode,
    applyIsLongTerm: apply.isLongTerm,
    isLongTerm: draft.isLongTerm,
    applyIsHidden: apply.isHidden,
    isHidden: draft.isHidden,
    applyTeams: apply.teams,
    teamIds: draft.teams.map((team) => team.id),
    applyCategories: apply.categories,
    categoryIds: draft.categoryIds,
    applyPopulations: apply.populations,
    populationIds: draft.populationIds,
    applyRegistrationFlow: apply.registrationFlow,
    volunteerRegistrationFlow: draft.volunteerRegistrationFlow,
    applyMatchWeight: apply.matchWeight,
    defaultMatchWeight: draft.defaultMatchWeight,
    applyDailyTarget: apply.dailyTarget,
    dailyVolunteerTarget,
    applyManagerUrgency: apply.managerUrgency,
    managerUrgency: draft.managerUrgency,
    applyCallCenterUrgency: apply.callCenterUrgency,
    callCenterUrgency: draft.callCenterUrgency,
    applyCallCenterDifficulty: apply.callCenterDifficulty,
    callCenterDifficulty: draft.callCenterDifficulty,
    applyWhatsapp: apply.whatsapp,
    whatsappGroupUrl: draft.whatsappGroupUrl,
  };
}

function anyApply(apply: BulkApply) {
  return Object.values(apply).some(Boolean);
}

const CONTENT_PREVIEW: {
  key: keyof BulkApply;
  label: string;
  value: (draft: BulkEditDraft, write: GroupChildBulkWrite) => string;
}[] = [
  { key: "title", label: "כותרת", value: (draft) => draft.title },
  { key: "description", label: "תיאור", value: (draft) => draft.description },
  { key: "image", label: "תמונה", value: () => "התמונה שנבחרה" },
  { key: "amount", label: "כמות", value: (draft) => String(draft.amount) },
  {
    key: "requester",
    label: "מבקש הסיוע",
    value: (draft) =>
      draft.associationRequested?.user?.name || "בלי מבקש סיוע",
  },
  {
    key: "comments",
    label: "הערות",
    value: (_draft, write) => formatBulkContentValue("comments", write),
  },
  {
    key: "age",
    label: "גיל",
    value: (_draft, write) => formatBulkContentValue("minAge", write),
  },
  {
    key: "requiredGender",
    label: "מגדר",
    value: (_draft, write) => formatBulkContentValue("requiredGender", write),
  },
  {
    key: "requiresVehicle",
    label: "רכב",
    value: (_draft, write) => formatBulkContentValue("requiresVehicle", write),
  },
  {
    key: "isSensitive",
    label: "משימה רגישה",
    value: (_draft, write) => formatBulkContentValue("isSensitive", write),
  },
  {
    key: "volunteerMode",
    label: "סוג התנדבות",
    value: (_draft, write) => formatBulkContentValue("volunteerMode", write),
  },
  {
    key: "isLongTerm",
    label: "התנדבות ממושכת",
    value: (_draft, write) => formatBulkContentValue("isLongTerm", write),
  },
  {
    key: "isHidden",
    label: "מוסתר ממתנדבים",
    value: (_draft, write) => formatBulkContentValue("isHidden", write),
  },
  {
    key: "teams",
    label: "פרויקטים",
    value: (draft) =>
      draft.teams.map((team) => team.name).filter(Boolean).join(", ") || "בלי פרויקטים",
  },
  {
    key: "categories",
    label: "קטגוריה",
    value: (draft) => draft.categoryIds.join(", ") || "בלי קטגוריה",
  },
  {
    key: "populations",
    label: "אוכלוסייה",
    value: (draft) => draft.populationIds.join(", ") || "בלי אוכלוסייה",
  },
  {
    key: "registrationFlow",
    label: "זרימת הרשמה",
    value: (_draft, write) =>
      formatBulkContentValue("volunteerRegistrationFlow", write),
  },
  {
    key: "matchWeight",
    label: "משקל מאץ'",
    value: (_draft, write) => formatBulkContentValue("defaultMatchWeight", write),
  },
  {
    key: "dailyTarget",
    label: "יעד יומי",
    value: (_draft, write) => formatBulkContentValue("dailyVolunteerTarget", write),
  },
  {
    key: "managerUrgency",
    label: "דחיפות מנהל",
    value: (_draft, write) => formatBulkContentValue("managerUrgency", write),
  },
  {
    key: "callCenterUrgency",
    label: "רמת דחיפות",
    value: (_draft, write) => formatBulkContentValue("callCenterUrgency", write),
  },
  {
    key: "callCenterDifficulty",
    label: "רמת קושי",
    value: (_draft, write) =>
      formatBulkContentValue("callCenterDifficulty", write),
  },
  {
    key: "whatsapp",
    label: "וואטסאפ",
    value: (_draft, write) => formatBulkContentValue("whatsappGroupUrl", write),
  },
];

function teamIdsOf(row: Opportunity): string[] {
  return (row.teams ?? [])
    .map((link) => link.teamId || link.team?.id || "")
    .filter(Boolean);
}

function categoryIdsOf(row: Opportunity): string[] {
  return (row.categories ?? []).map((link) => link.categoryId).filter(Boolean);
}

function populationIdsOf(row: Opportunity): string[] {
  return (row.populations ?? [])
    .map((link) => link.populationId)
    .filter(Boolean);
}

function sensitiveRelationWarnings(
  draft: BulkEditDraft,
  apply: BulkApply,
  rows: Opportunity[],
): string[] {
  const checks: { on: boolean; label: string; next: string[]; current: string[][] }[] =
    [
      {
        on: apply.teams,
        label: "הפרויקטים",
        next: draft.teams.map((team) => team.id),
        current: rows.map(teamIdsOf),
      },
      {
        on: apply.categories,
        label: "הקטגוריות",
        next: draft.categoryIds,
        current: rows.map(categoryIdsOf),
      },
      {
        on: apply.populations,
        label: "האוכלוסיות",
        next: draft.populationIds,
        current: rows.map(populationIdsOf),
      },
    ];
  return checks.flatMap((check) => {
    if (!check.on) return [];
    const message = bulkRelationSensitiveWarning({
      fieldLabel: check.label,
      nextIds: check.next,
      currentSets: check.current,
    });
    return message ? [message] : [];
  });
}

function SensitiveRelationAlert({ messages }: { messages: string[] }) {
  if (messages.length === 0) return null;
  const clears = messages.some((message) => message.startsWith("שמירה תמחק"));
  return (
    <Alert severity={clears ? "error" : "warning"}>
      <Typography variant="subtitle2">{ADMIN_BULK_EDIT_SENSITIVE_TITLE}</Typography>
      {messages.map((message) => (
        <Typography key={message} variant="body2">
          {message}
        </Typography>
      ))}
      <Typography variant="body2">{ADMIN_BULK_EDIT_SENSITIVE_HINT}</Typography>
    </Alert>
  );
}

function BulkEditPreview({
  draft,
  apply,
  selectedChildren,
  appliesTo,
  previewSubject,
}: {
  draft: BulkEditDraft;
  apply: BulkApply;
  selectedChildren: Opportunity[];
  appliesTo: (count: number) => string;
  previewSubject: string;
}) {
  const { currOrganization } = useOrganization();
  const patch = patchFromDraft(draft, apply);
  const write = selectGroupChildBulkUpdateWrite(
    buildGroupChildBulkUpdateFields(patch, new Date()),
    patch,
  );
  const categoryName = (id: string) =>
    currOrganization?.categories?.find((category) => category.id === id)?.name ||
    id;
  const populationName = (id: string) =>
    currOrganization?.populations?.find((population) => population.id === id)
      ?.name || id;
  const contentLines = CONTENT_PREVIEW.filter((row) => apply[row.key]).map(
    (row) => {
      if (row.key === "categories") {
        return {
          ...row,
          text:
            draft.categoryIds.map(categoryName).join(", ") || "בלי קטגוריה",
        };
      }
      if (row.key === "populations") {
        return {
          ...row,
          text:
            draft.populationIds.map(populationName).join(", ") ||
            "בלי אוכלוסייה",
        };
      }
      return { ...row, text: row.value(draft, write) };
    },
  );
  const showCriticalTable =
    apply.schedule || apply.location || apply.lastCall;
  const sensitiveWarnings = sensitiveRelationWarnings(
    draft,
    apply,
    selectedChildren,
  );

  return (
    <Stack gap={2} sx={{ pt: 1 }}>
      <SensitiveRelationAlert messages={sensitiveWarnings} />
      {contentLines.map((row) => (
        <Typography key={row.key} variant="body2">
          {row.label}: {row.text}. {appliesTo(selectedChildren.length)}
        </Typography>
      ))}
      {!apply.schedule && (
        <Typography variant="body2">
          {ADMIN_GROUP_BULK_EDIT_SCHEDULE_UNCHANGED}
        </Typography>
      )}
      {!apply.location && (
        <Typography variant="body2">
          {ADMIN_GROUP_BULK_EDIT_LOCATION_UNCHANGED}
        </Typography>
      )}
      {!apply.lastCall && (
        <Typography variant="body2">
          {ADMIN_GROUP_BULK_EDIT_LAST_CALL_UNCHANGED}
        </Typography>
      )}
      {showCriticalTable && (
        <Alert severity="warning">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{previewSubject}</TableCell>
                {apply.schedule && <TableCell>מועד עכשיו</TableCell>}
                {apply.schedule && <TableCell>מועד אחרי</TableCell>}
                {apply.location && <TableCell>מיקום עכשיו</TableCell>}
                {apply.location && <TableCell>מיקום אחרי</TableCell>}
                {apply.lastCall && <TableCell>מועד אחרון עכשיו</TableCell>}
                {apply.lastCall && <TableCell>מועד אחרון אחרי</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {selectedChildren.map((child) => (
                <TableRow key={child.id}>
                  <TableCell>{formatBulkLocationSummary(child)}</TableCell>
                  {apply.schedule && (
                    <TableCell>{formatBulkScheduleSummary(child)}</TableCell>
                  )}
                  {apply.schedule && (
                    <TableCell>{formatBulkScheduleSummary(draft)}</TableCell>
                  )}
                  {apply.location && (
                    <TableCell>{formatBulkLocationSummary(child)}</TableCell>
                  )}
                  {apply.location && (
                    <TableCell>{formatBulkLocationSummary(draft)}</TableCell>
                  )}
                  {apply.lastCall && (
                    <TableCell>
                      {formatBulkLastCallSummary(child.lastCallToVolunteers)}
                    </TableCell>
                  )}
                  {apply.lastCall && (
                    <TableCell>
                      {formatBulkLastCallSummary(draft.lastCallToVolunteers)}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Alert>
      )}
      <Typography variant="caption" color="text.secondary">
        {ADMIN_GROUP_BULK_EDIT_UNCHANGED} בשדות שלא מופיעים כאן כשינוי.
      </Typography>
    </Stack>
  );
}

interface GroupOpportunitySlotsBulkEditProps {
  open: boolean;
  onClose: () => void;
  parentId?: string;
  selectedChildren: Opportunity[];
  onSuccess: () => void;
  onSave?: (
    patch: GroupChildBulkUpdatePatchInput,
  ) => Promise<{ success: boolean; message: string }>;
  dialogTitle?: string;
  hint?: string;
  appliesTo?: (count: number) => string;
  maxMessage?: (max: number) => string;
  saveError?: string;
  previewSubject?: string;
}

export const GroupOpportunitySlotsBulkEdit: FC<
  GroupOpportunitySlotsBulkEditProps
> = ({
  open,
  onClose,
  parentId,
  selectedChildren,
  onSuccess,
  onSave,
  dialogTitle = ADMIN_GROUP_BULK_EDIT_TITLE,
  hint = ADMIN_GROUP_BULK_EDIT_HINT,
  appliesTo = ADMIN_GROUP_BULK_EDIT_APPLIES_TO,
  maxMessage = ADMIN_GROUP_BULK_EDIT_MAX,
  saveError = ADMIN_GROUP_BULK_EDIT_ERROR,
  previewSubject = "מופע",
}) => {
  const { setAlertText, setSeverity } = useAlert();
  const [draft, setDraft] = useState<BulkEditDraft | null>(null);
  const [apply, setApply] = useState<BulkApply>(APPLY_OFF);
  const [step, setStep] = useState<"edit" | "preview">("edit");
  const [isSaving, setIsSaving] = useState(false);
  const [isAddAidRelated, setIsAddAidRelated] = useState(false);

  useEffect(() => {
    if (!open) {
      setDraft(null);
      setIsAddAidRelated(false);
      setApply(APPLY_OFF);
      setStep("edit");
      setTeamsAnchor(null);
      return;
    }
    const source = selectedChildren[0];
    if (!source) return;
    setDraft(draftFromOpportunity(source));
    // Seed only when the dialog opens so parent refetches do not reset edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- selectedChildren captured at open
  }, [open]);

  useEffect(() => {
    if (!open || !draft?.associationRequestedId || draft.associationRequested?.user) {
      return;
    }
    let cancelled = false;
    void associationRepo
      .findId(draft.associationRequestedId, { include: { user: true } })
      .then((association) => {
        if (cancelled || !association) return;
        setDraft((prev) =>
          prev
            ? {
                ...prev,
                associationRequested: association,
                associationRequestedId: association.id,
              }
            : prev,
        );
      });
    return () => {
      cancelled = true;
    };
  }, [open, draft?.associationRequestedId, draft?.associationRequested?.user]);

  const selectedCount = selectedChildren.length;
  const overMax = selectedCount > GROUP_INSTANCE_BULK_MAX;
  const { currOrganization } = useOrganization();
  const [teamsAnchor, setTeamsAnchor] = useState<HTMLButtonElement | null>(
    null,
  );
  const childIds = useMemo(
    () => selectedChildren.map((child) => child.id),
    [selectedChildren],
  );

  const scheduleDateValue = draft?.hasTime
    ? draft.hasStartTime
      ? draft.startDate
      : draft.endDate
    : null;

  const applyScheduleDate = (date: Date) => {
    setDraft((prev) => {
      if (!prev) return prev;
      const next = applyPickedCalendarDate({
        hasStartTime: prev.hasStartTime,
        startDate: prev.startDate,
        endDate: prev.endDate,
        picked: date,
      });
      return { ...prev, ...next };
    });
  };

  const applyScheduleTime = (field: "startDate" | "endDate", picked: Date) => {
    setDraft((prev) => {
      if (!prev) return prev;
      const base =
        field === "startDate"
          ? prev.startDate
            ? new Date(prev.startDate)
            : new Date()
          : prev.endDate
            ? new Date(prev.endDate)
            : prev.startDate
              ? new Date(prev.startDate)
              : new Date();
      base.setHours(picked.getHours(), picked.getMinutes(), 0, 0);
      if (field === "startDate") {
        const next = { ...prev, startDate: base };
        if (next.endDate && next.endDate < base) {
          next.endDate = new Date(base.getTime() + 2 * 60 * 60 * 1000);
        }
        return next;
      }
      return { ...prev, endDate: base };
    });
  };

  const openPreview = () => {
    if (!draft) return;
    if (!anyApply(apply)) {
      setSeverity("warning");
      setAlertText(ADMIN_GROUP_BULK_EDIT_NOTHING);
      return;
    }
    const validationError = validateGroupChildBulkUpdatePatch(
      patchFromDraft(draft, apply),
    );
    if (validationError) {
      setSeverity("warning");
      setAlertText(validationError);
      return;
    }
    setStep("preview");
  };

  const handleSave = async () => {
    if (!draft) return;
    if (overMax) {
      setSeverity("warning");
      setAlertText(maxMessage(GROUP_INSTANCE_BULK_MAX));
      return;
    }
    const patch = patchFromDraft(draft, apply);
    const validationError = validateGroupChildBulkUpdatePatch(patch);
    if (validationError) {
      setSeverity("warning");
      setAlertText(validationError);
      return;
    }

    setIsSaving(true);
    try {
      const res = onSave
        ? await onSave(patch)
        : await Opportunity.bulkUpdateGroupChildSlots(
            parentId ?? "",
            childIds,
            patch,
          );
      setSeverity(res.success ? "success" : "error");
      setAlertText(res.message);
      if (res.success) {
        onSuccess();
        onClose();
      }
    } catch {
      setSeverity("error");
      setAlertText(saveError);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={step === "preview" ? "md" : "sm"}
    >
      <DialogTitle>{dialogTitle}</DialogTitle>
      <DialogContent>
        {!draft ? (
          <CircularProgress size={24} />
        ) : step === "preview" ? (
          <BulkEditPreview
            draft={draft}
            apply={apply}
            selectedChildren={selectedChildren}
            appliesTo={appliesTo}
            previewSubject={previewSubject}
          />
        ) : (
          <Stack gap={2} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {hint}
            </Typography>
            <SensitiveRelationAlert
              messages={sensitiveRelationWarnings(draft, apply, selectedChildren)}
            />
            {overMax && (
              <Alert severity="warning">
                {maxMessage(GROUP_INSTANCE_BULK_MAX)}
              </Alert>
            )}
            <Typography variant="subtitle2">
              {ADMIN_GROUP_BULK_EDIT_CONTENT_TITLE}
            </Typography>
            <FormControlLabel
              sx={{ m: 0, alignSelf: "flex-start" }}
              control={
                <Checkbox
                  size="small"
                  checked={apply.image}
                  onChange={(e) =>
                    setApply((prev) => ({ ...prev, image: e.target.checked }))
                  }
                />
              }
              label={<Typography variant="body2">עדכן תמונה</Typography>}
            />
            {apply.image && (
            <EventImageSection
              imageId={draft.imageId}
              onImageIdChange={(imageId) =>
                setDraft((prev) => (prev ? { ...prev, imageId } : prev))
              }
            />
            )}
            <FormControlLabel
              sx={{ m: 0, alignSelf: "flex-start" }}
              control={
                <Checkbox
                  size="small"
                  checked={apply.title}
                  onChange={(e) =>
                    setApply((prev) => ({ ...prev, title: e.target.checked }))
                  }
                />
              }
              label={<Typography variant="body2">עדכן כותרת</Typography>}
            />
            {apply.title && (
            <TextField
              label="כותרת המשימה למתנדבים"
              value={draft.title}
              onChange={(e) =>
                setDraft((prev) =>
                  prev
                    ? {
                        ...prev,
                        title: e.target.value.slice(
                          0,
                          OPPORTUNITY_TITLE_MAX_LENGTH,
                        ),
                      }
                    : prev,
                )
              }
              inputProps={{ maxLength: OPPORTUNITY_TITLE_MAX_LENGTH }}
              fullWidth
            />
            )}
            <FormControlLabel
              sx={{ m: 0, alignSelf: "flex-start" }}
              control={
                <Checkbox
                  size="small"
                  checked={apply.description}
                  onChange={(e) =>
                    setApply((prev) => ({ ...prev, description: e.target.checked }))
                  }
                />
              }
              label={<Typography variant="body2">עדכן תיאור</Typography>}
            />
            {apply.description && (
            <TextField
              label="תיאור המשימה למתנדבים"
              value={draft.description}
              onChange={(e) =>
                setDraft((prev) =>
                  prev
                    ? {
                        ...prev,
                        description: e.target.value.slice(
                          0,
                          VOLUNTEER_DESCRIPTION_MAX_LENGTH,
                        ),
                      }
                    : prev,
                )
              }
              inputProps={{ maxLength: VOLUNTEER_DESCRIPTION_MAX_LENGTH }}
              multiline
              minRows={3}
              maxRows={8}
              fullWidth
            />
            )}
            <FormControlLabel
              sx={{ m: 0, alignSelf: "flex-start" }}
              control={
                <Checkbox
                  size="small"
                  checked={apply.amount}
                  onChange={(e) =>
                    setApply((prev) => ({ ...prev, amount: e.target.checked }))
                  }
                />
              }
              label={<Typography variant="body2">עדכן כמות</Typography>}
            />
            {apply.amount && (
            <TextField
              label="כמות משתתפים"
              type="number"
              size="small"
              value={draft.amount}
              onChange={(e) =>
                setDraft((prev) =>
                  prev
                    ? {
                        ...prev,
                        amount: Math.max(1, Number(e.target.value) || 1),
                      }
                    : prev,
                )
              }
              inputProps={{ min: 1, step: 1 }}
              fullWidth
            />
            )}
            <FormControlLabel
              sx={{ m: 0, alignSelf: "flex-start" }}
              control={
                <Checkbox
                  size="small"
                  checked={apply.requester}
                  onChange={(e) =>
                    setApply((prev) => ({ ...prev, requester: e.target.checked }))
                  }
                />
              }
              label={<Typography variant="body2">עדכן מבקש סיוע</Typography>}
            />
            {apply.requester && (
            <Box>
              <TwoCheckbox
                isChecked={!!draft.associationRequestedId || !!draft.associationRequested}
                handleChange={(event) => {
                  const isYes = event.target.name === "yes";
                  setDraft((prev) => {
                    if (!prev) return prev;
                    if (!isYes) {
                      return {
                        ...prev,
                        associationRequested: undefined,
                        associationRequestedId: null,
                      };
                    }
                    if (prev.associationRequestedId || prev.associationRequested?.id) {
                      return prev;
                    }
                    return {
                      ...prev,
                      associationRequested: {} as Association,
                      associationRequestedId: null,
                    };
                  });
                }}
                text="שיוך למבקש הסיוע"
              />
              {(!!draft.associationRequestedId || !!draft.associationRequested) && (
                <RequesterDetails
                  setIsAddAidRelated={setIsAddAidRelated}
                  requesterDetails={draft.associationRequested?.user}
                />
              )}
            </Box>
            )}
            <FormControlLabel
              sx={{ m: 0, alignSelf: "flex-start" }}
              control={
                <Checkbox
                  size="small"
                  checked={apply.comments}
                  onChange={(e) =>
                    setApply((prev) => ({ ...prev, comments: e.target.checked }))
                  }
                />
              }
              label={<Typography variant="body2">עדכן הערות</Typography>}
            />
            {apply.comments && (
              <TextField
                label="הערות"
                value={draft.comments}
                onChange={(e) =>
                  setDraft((prev) =>
                    prev ? { ...prev, comments: e.target.value } : prev,
                  )
                }
                multiline
                minRows={2}
                fullWidth
                size="small"
              />
            )}
            <FormControlLabel
              sx={{ m: 0, alignSelf: "flex-start" }}
              control={
                <Checkbox
                  size="small"
                  checked={apply.age}
                  onChange={(e) =>
                    setApply((prev) => ({ ...prev, age: e.target.checked }))
                  }
                />
              }
              label={<Typography variant="body2">עדכן גיל</Typography>}
            />
            {apply.age && (
              <Stack direction="row" gap={1}>
                <TextField
                  label="גיל מינימום"
                  type="number"
                  size="small"
                  value={draft.minAge}
                  onChange={(e) =>
                    setDraft((prev) =>
                      prev ? { ...prev, minAge: Number(e.target.value) } : prev,
                    )
                  }
                  fullWidth
                />
                <TextField
                  label="גיל מקסימום"
                  type="number"
                  size="small"
                  value={draft.maxAge}
                  onChange={(e) =>
                    setDraft((prev) =>
                      prev ? { ...prev, maxAge: Number(e.target.value) } : prev,
                    )
                  }
                  fullWidth
                />
              </Stack>
            )}
            <FormControlLabel
              sx={{ m: 0, alignSelf: "flex-start" }}
              control={
                <Checkbox
                  size="small"
                  checked={apply.requiredGender}
                  onChange={(e) =>
                    setApply((prev) => ({
                      ...prev,
                      requiredGender: e.target.checked,
                    }))
                  }
                />
              }
              label={<Typography variant="body2">עדכן מגדר</Typography>}
            />
            {apply.requiredGender && (
              <Select
                size="small"
                fullWidth
                value={draft.requiredGender ?? "ALL"}
                onChange={(e) =>
                  setDraft((prev) =>
                    prev
                      ? {
                          ...prev,
                          requiredGender:
                            e.target.value === "ALL"
                              ? null
                              : (e.target.value as "MALE" | "FEMALE"),
                        }
                      : prev,
                  )
                }
              >
                <MenuItem value="ALL">כולם</MenuItem>
                <MenuItem value="MALE">זכר</MenuItem>
                <MenuItem value="FEMALE">נקבה</MenuItem>
              </Select>
            )}
            <FormControlLabel
              sx={{ m: 0, alignSelf: "flex-start" }}
              control={
                <Checkbox
                  size="small"
                  checked={apply.requiresVehicle}
                  onChange={(e) =>
                    setApply((prev) => ({
                      ...prev,
                      requiresVehicle: e.target.checked,
                    }))
                  }
                />
              }
              label={<Typography variant="body2">עדכן דרישת רכב</Typography>}
            />
            {apply.requiresVehicle && (
              <Select
                size="small"
                fullWidth
                value={draft.requiresVehicle ? "yes" : "no"}
                onChange={(e) =>
                  setDraft((prev) =>
                    prev
                      ? { ...prev, requiresVehicle: e.target.value === "yes" }
                      : prev,
                  )
                }
              >
                <MenuItem value="yes">דורש רכב</MenuItem>
                <MenuItem value="no">לא דורש רכב</MenuItem>
              </Select>
            )}
            <FormControlLabel
              sx={{ m: 0, alignSelf: "flex-start" }}
              control={
                <Checkbox
                  size="small"
                  checked={apply.isSensitive}
                  onChange={(e) =>
                    setApply((prev) => ({ ...prev, isSensitive: e.target.checked }))
                  }
                />
              }
              label={<Typography variant="body2">עדכן משימה רגישה</Typography>}
            />
            {apply.isSensitive && (
              <Select
                size="small"
                fullWidth
                value={draft.isSensitive ? "yes" : "no"}
                onChange={(e) =>
                  setDraft((prev) =>
                    prev
                      ? { ...prev, isSensitive: e.target.value === "yes" }
                      : prev,
                  )
                }
              >
                <MenuItem value="yes">רגישה</MenuItem>
                <MenuItem value="no">לא רגישה</MenuItem>
              </Select>
            )}
            <FormControlLabel
              sx={{ m: 0, alignSelf: "flex-start" }}
              control={
                <Checkbox
                  size="small"
                  checked={apply.volunteerMode}
                  onChange={(e) =>
                    setApply((prev) => ({
                      ...prev,
                      volunteerMode: e.target.checked,
                    }))
                  }
                />
              }
              label={<Typography variant="body2">עדכן סוג התנדבות</Typography>}
            />
            {apply.volunteerMode && (
              <Select
                size="small"
                fullWidth
                value={draft.volunteerMode}
                onChange={(e) =>
                  setDraft((prev) =>
                    prev
                      ? {
                          ...prev,
                          volunteerMode: e.target
                            .value as OpportunityVolunteerModeType,
                        }
                      : prev,
                  )
                }
              >
                {Object.values(OpportunityVolunteerMode).map((mode) => (
                  <MenuItem key={mode} value={mode}>
                    {OPPORTUNITY_VOLUNTEER_MODE_HEB[mode]}
                  </MenuItem>
                ))}
              </Select>
            )}
            <FormControlLabel
              sx={{ m: 0, alignSelf: "flex-start" }}
              control={
                <Checkbox
                  size="small"
                  checked={apply.isLongTerm}
                  onChange={(e) =>
                    setApply((prev) => ({ ...prev, isLongTerm: e.target.checked }))
                  }
                />
              }
              label={<Typography variant="body2">עדכן ארוך טווח</Typography>}
            />
            {apply.isLongTerm && (
              <Select
                size="small"
                fullWidth
                value={draft.isLongTerm ? "yes" : "no"}
                onChange={(e) =>
                  setDraft((prev) =>
                    prev
                      ? { ...prev, isLongTerm: e.target.value === "yes" }
                      : prev,
                  )
                }
              >
                <MenuItem value="yes">ארוך טווח</MenuItem>
                <MenuItem value="no">לא ארוך טווח</MenuItem>
              </Select>
            )}
            <FormControlLabel
              sx={{ m: 0, alignSelf: "flex-start" }}
              control={
                <Checkbox
                  size="small"
                  checked={apply.isHidden}
                  onChange={(e) =>
                    setApply((prev) => ({ ...prev, isHidden: e.target.checked }))
                  }
                />
              }
              label={<Typography variant="body2">עדכן מוסתר ממתנדבים</Typography>}
            />
            {apply.isHidden && (
              <Select
                size="small"
                fullWidth
                value={draft.isHidden ? "yes" : "no"}
                onChange={(e) =>
                  setDraft((prev) =>
                    prev ? { ...prev, isHidden: e.target.value === "yes" } : prev,
                  )
                }
              >
                <MenuItem value="yes">מוסתר ממתנדבים</MenuItem>
                <MenuItem value="no">גלוי למתנדבים</MenuItem>
              </Select>
            )}
            <FormControlLabel
              sx={{ m: 0, alignSelf: "flex-start" }}
              control={
                <Checkbox
                  size="small"
                  checked={apply.teams}
                  onChange={(e) =>
                    setApply((prev) => ({ ...prev, teams: e.target.checked }))
                  }
                />
              }
              label={<Typography variant="body2">עדכן פרויקטים</Typography>}
            />
            {apply.teams && (
              <Stack gap={1}>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {draft.teams.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      בלי פרויקטים
                    </Typography>
                  ) : (
                    draft.teams.map((team) => (
                      <Chip key={team.id} size="small" label={team.name} />
                    ))
                  )}
                </Box>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={(e) => setTeamsAnchor(e.currentTarget)}
                >
                  בחירת פרויקטים
                </Button>
              </Stack>
            )}
            <FormControlLabel
              sx={{ m: 0, alignSelf: "flex-start" }}
              control={
                <Checkbox
                  size="small"
                  checked={apply.categories}
                  onChange={(e) =>
                    setApply((prev) => ({
                      ...prev,
                      categories: e.target.checked,
                    }))
                  }
                />
              }
              label={<Typography variant="body2">עדכן קטגוריה</Typography>}
            />
            {apply.categories && (
              <Select
                multiple
                size="small"
                fullWidth
                value={draft.categoryIds}
                onChange={(e) => {
                  const value = e.target.value;
                  const categoryIds =
                    typeof value === "string" ? value.split(",") : value;
                  setDraft((prev) => (prev ? { ...prev, categoryIds } : prev));
                }}
                renderValue={(selected) =>
                  (selected as string[])
                    .map(
                      (id) =>
                        currOrganization?.categories?.find(
                          (category) => category.id === id,
                        )?.name || id,
                    )
                    .join(", ") || "בלי קטגוריה"
                }
              >
                {(currOrganization?.categories ?? [])
                  .slice()
                  .sort(compareBySortOrder)
                  .map((category) => (
                    <MenuItem key={category.id} value={category.id}>
                      <Checkbox
                        size="small"
                        checked={draft.categoryIds.includes(category.id)}
                      />
                      {category.name}
                    </MenuItem>
                  ))}
              </Select>
            )}
            <FormControlLabel
              sx={{ m: 0, alignSelf: "flex-start" }}
              control={
                <Checkbox
                  size="small"
                  checked={apply.populations}
                  onChange={(e) =>
                    setApply((prev) => ({
                      ...prev,
                      populations: e.target.checked,
                    }))
                  }
                />
              }
              label={<Typography variant="body2">עדכן אוכלוסייה</Typography>}
            />
            {apply.populations && (
              <Select
                multiple
                size="small"
                fullWidth
                value={draft.populationIds}
                onChange={(e) => {
                  const value = e.target.value;
                  const populationIds =
                    typeof value === "string" ? value.split(",") : value;
                  setDraft((prev) =>
                    prev ? { ...prev, populationIds } : prev,
                  );
                }}
                renderValue={(selected) =>
                  (selected as string[])
                    .map(
                      (id) =>
                        currOrganization?.populations?.find(
                          (population) => population.id === id,
                        )?.name || id,
                    )
                    .join(", ") || "בלי אוכלוסייה"
                }
              >
                {(currOrganization?.populations ?? [])
                  .slice()
                  .sort((a, b) => a.name.localeCompare(b.name, "he"))
                  .map((population) => (
                    <MenuItem key={population.id} value={population.id}>
                      <Checkbox
                        size="small"
                        checked={draft.populationIds.includes(population.id)}
                      />
                      {population.name}
                    </MenuItem>
                  ))}
              </Select>
            )}
            <FormControlLabel
              sx={{ m: 0, alignSelf: "flex-start" }}
              control={
                <Checkbox
                  size="small"
                  checked={apply.registrationFlow}
                  onChange={(e) =>
                    setApply((prev) => ({
                      ...prev,
                      registrationFlow: e.target.checked,
                    }))
                  }
                />
              }
              label={<Typography variant="body2">עדכן זרימת הרשמה</Typography>}
            />
            {apply.registrationFlow && (
              <Select
                size="small"
                fullWidth
                value={draft.volunteerRegistrationFlow}
                onChange={(e) =>
                  setDraft((prev) =>
                    prev
                      ? {
                          ...prev,
                          volunteerRegistrationFlow: e.target
                            .value as VolunteerRegistrationOutcomeType,
                        }
                      : prev,
                  )
                }
              >
                <MenuItem value={VolunteerRegistrationOutcome.SMS_DETAILS}>
                  הרשמה ופרטי מבקש ב-SMS
                </MenuItem>
                <MenuItem value={VolunteerRegistrationOutcome.REGULAR}>
                  הרשמה רגילה
                </MenuItem>
                <MenuItem value={VolunteerRegistrationOutcome.INTERESTED}>
                  התעניינות (ממתין לאישור)
                </MenuItem>
                <MenuItem value={VolunteerRegistrationOutcome.CHAT}>
                  הרשמה וצ'אט עם מבקש הסיוע
                </MenuItem>
              </Select>
            )}
            <FormControlLabel
              sx={{ m: 0, alignSelf: "flex-start" }}
              control={
                <Checkbox
                  size="small"
                  checked={apply.matchWeight}
                  onChange={(e) =>
                    setApply((prev) => ({ ...prev, matchWeight: e.target.checked }))
                  }
                />
              }
              label={<Typography variant="body2">עדכן משקל מאץ'</Typography>}
            />
            {apply.matchWeight && (
              <TextField
                label="משקל מאץ'"
                type="number"
                size="small"
                value={draft.defaultMatchWeight}
                onChange={(e) =>
                  setDraft((prev) =>
                    prev
                      ? { ...prev, defaultMatchWeight: Number(e.target.value) }
                      : prev,
                  )
                }
                inputProps={{ min: MATCH_WEIGHT_MIN, max: MATCH_WEIGHT_MAX }}
                fullWidth
              />
            )}
            <FormControlLabel
              sx={{ m: 0, alignSelf: "flex-start" }}
              control={
                <Checkbox
                  size="small"
                  checked={apply.dailyTarget}
                  onChange={(e) =>
                    setApply((prev) => ({ ...prev, dailyTarget: e.target.checked }))
                  }
                />
              }
              label={<Typography variant="body2">עדכן יעד יומי</Typography>}
            />
            {apply.dailyTarget && (
              <TextField
                label="יעד מתנדבים יומי"
                size="small"
                value={draft.dailyVolunteerTarget}
                placeholder="ריק = בלי יעד"
                onChange={(e) =>
                  setDraft((prev) =>
                    prev
                      ? { ...prev, dailyVolunteerTarget: e.target.value }
                      : prev,
                  )
                }
                fullWidth
              />
            )}
            <FormControlLabel
              sx={{ m: 0, alignSelf: "flex-start" }}
              control={
                <Checkbox
                  size="small"
                  checked={apply.managerUrgency}
                  onChange={(e) =>
                    setApply((prev) => ({
                      ...prev,
                      managerUrgency: e.target.checked,
                    }))
                  }
                />
              }
              label={<Typography variant="body2">עדכן דחיפות מנהל</Typography>}
            />
            {apply.managerUrgency && (
              <Select
                size="small"
                fullWidth
                value={draft.managerUrgency}
                onChange={(e) =>
                  setDraft((prev) =>
                    prev
                      ? {
                          ...prev,
                          managerUrgency: e.target.value as ManagerUrgency,
                        }
                      : prev,
                  )
                }
              >
                <MenuItem value="STANDARD">רגילה</MenuItem>
                <MenuItem value="HIGH">גבוהה</MenuItem>
                <MenuItem value="CRITICAL">קריטית</MenuItem>
              </Select>
            )}
            <FormControlLabel
              sx={{ m: 0, alignSelf: "flex-start" }}
              control={
                <Checkbox
                  size="small"
                  checked={apply.callCenterUrgency}
                  onChange={(e) =>
                    setApply((prev) => ({
                      ...prev,
                      callCenterUrgency: e.target.checked,
                    }))
                  }
                />
              }
              label={<Typography variant="body2">עדכן רמת דחיפות</Typography>}
            />
            {apply.callCenterUrgency && (
              <Select
                size="small"
                fullWidth
                value={draft.callCenterUrgency}
                onChange={(e) =>
                  setDraft((prev) =>
                    prev
                      ? { ...prev, callCenterUrgency: Number(e.target.value) }
                      : prev,
                  )
                }
              >
                {Array.from({ length: 11 }, (_, value) => (
                  <MenuItem key={value} value={value}>
                    {value === 0 ? "0 - ללא" : String(value)}
                  </MenuItem>
                ))}
              </Select>
            )}
            <FormControlLabel
              sx={{ m: 0, alignSelf: "flex-start" }}
              control={
                <Checkbox
                  size="small"
                  checked={apply.callCenterDifficulty}
                  onChange={(e) =>
                    setApply((prev) => ({
                      ...prev,
                      callCenterDifficulty: e.target.checked,
                    }))
                  }
                />
              }
              label={<Typography variant="body2">עדכן רמת קושי</Typography>}
            />
            {apply.callCenterDifficulty && (
              <Select
                size="small"
                fullWidth
                value={draft.callCenterDifficulty}
                onChange={(e) =>
                  setDraft((prev) =>
                    prev
                      ? {
                          ...prev,
                          callCenterDifficulty: Number(e.target.value),
                        }
                      : prev,
                  )
                }
              >
                {[1, 2, 3, 4, 5].map((value) => (
                  <MenuItem key={value} value={value}>
                    {value === 1 || value === 2
                      ? `${value} - קלה`
                      : value === 3
                        ? "3 - בינונית"
                        : `${value} - קשה`}
                  </MenuItem>
                ))}
              </Select>
            )}
            <FormControlLabel
              sx={{ m: 0, alignSelf: "flex-start" }}
              control={
                <Checkbox
                  size="small"
                  checked={apply.whatsapp}
                  onChange={(e) =>
                    setApply((prev) => ({ ...prev, whatsapp: e.target.checked }))
                  }
                />
              }
              label={<Typography variant="body2">עדכן קישור וואטסאפ</Typography>}
            />
            {apply.whatsapp && (
              <TextField
                label="קישור וואטסאפ"
                size="small"
                value={draft.whatsappGroupUrl}
                onChange={(e) =>
                  setDraft((prev) =>
                    prev ? { ...prev, whatsappGroupUrl: e.target.value } : prev,
                  )
                }
                fullWidth
              />
            )}
            <Alert severity="warning">
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {ADMIN_GROUP_BULK_EDIT_CRITICAL_TITLE}
            </Typography>
            <FormControlLabel
              sx={{ m: 0, alignSelf: "flex-start" }}
              control={
                <Checkbox
                  size="small"
                  checked={apply.schedule}
                  onChange={(e) =>
                    setApply((prev) => ({ ...prev, schedule: e.target.checked }))
                  }
                />
              }
              label={
                <Typography variant="body2">
                  {ADMIN_GROUP_BULK_EDIT_APPLY_SCHEDULE}
                </Typography>
              }
            />
            {apply.schedule && (
            <Stack gap={1}>
            <Alert severity="info">{ADMIN_GROUP_BULK_EDIT_TIME_WARNING}</Alert>
            <LocalizationProvider
              dateAdapter={AdapterDayjs}
              localeText={
                heIL.components.MuiLocalizationProvider.defaultProps.localeText
              }
              adapterLocale="he"
            >
              <Stack gap={1}>
                {draft.hasTime && (
                  <FormControlLabel
                    sx={{ m: 0, alignSelf: "flex-start" }}
                    control={
                      <Checkbox
                        size="small"
                        checked={!draft.hasStartTime}
                        onChange={(e) => {
                          const toUntil = e.target.checked;
                          setDraft((prev) =>
                            prev
                              ? {
                                  ...prev,
                                  hasStartTime: !toUntil,
                                  startDate: toUntil
                                    ? null
                                    : (prev.startDate ??
                                      prev.endDate ??
                                      new Date()),
                                  endDate:
                                    prev.endDate ??
                                    prev.startDate ??
                                    new Date(),
                                }
                              : prev,
                          );
                        }}
                      />
                    }
                    label={
                      <Typography variant="body2">לביצוע עד</Typography>
                    }
                  />
                )}
                <FormControlLabel
                  sx={{ m: 0, alignSelf: "flex-start" }}
                  control={
                    <Checkbox
                      size="small"
                      checked={!draft.hasTime}
                      onChange={(e) => {
                        const noTime = e.target.checked;
                        setDraft((prev) =>
                          prev
                            ? {
                                ...prev,
                                hasTime: !noTime,
                                timeDescription: noTime
                                  ? prev.timeDescription
                                  : "",
                                startDate: noTime
                                  ? null
                                  : (prev.startDate ?? new Date()),
                                endDate: noTime
                                  ? null
                                  : (prev.endDate ?? new Date()),
                                hasStartTime: true,
                              }
                            : prev,
                        );
                      }}
                    />
                  }
                  label={
                    <Typography variant="body2">ללא זמן מוגדר</Typography>
                  }
                />
                {draft.hasTime ? (
                  <Stack gap={1.25}>
                    <DatePicker
                      label={draft.hasStartTime ? "בתאריך" : "עד תאריך"}
                      format="DD/MM/YYYY"
                      value={
                        scheduleDateValue ? dayjs(scheduleDateValue) : dayjs()
                      }
                      onChange={(value: DateEvent) => {
                        if (!value || !isValid(value.toDate())) return;
                        applyScheduleDate(new Date(value.$d ?? value.toDate()));
                      }}
                      slotProps={{
                        textField: { size: "small", fullWidth: true },
                      }}
                    />
                    {draft.hasStartTime && (
                      <TimePicker
                        label="משעה"
                        format="HH:mm"
                        timeSteps={{ minutes: 15 }}
                        value={
                          draft.startDate
                            ? dayjs(draft.startDate)
                            : dayjs().hour(9).minute(0)
                        }
                        onChange={(value: DateEvent) => {
                          if (!value || !isValid(value.toDate())) return;
                          applyScheduleTime(
                            "startDate",
                            new Date(value.$d ?? value.toDate()),
                          );
                        }}
                        slotProps={{
                          textField: { size: "small", fullWidth: true },
                        }}
                      />
                    )}
                    <TimePicker
                      label={draft.hasStartTime ? "עד שעה" : "בשעה"}
                      format="HH:mm"
                      timeSteps={{ minutes: 10 }}
                      minTime={
                        draft.hasStartTime && draft.startDate
                          ? dayjs(draft.startDate)
                          : undefined
                      }
                      value={
                        draft.endDate
                          ? dayjs(draft.endDate)
                          : dayjs().hour(11).minute(0)
                      }
                      onChange={(value: DateEvent) => {
                        if (!value || !isValid(value.toDate())) return;
                        applyScheduleTime(
                          "endDate",
                          new Date(value.$d ?? value.toDate()),
                        );
                      }}
                      slotProps={{
                        textField: { size: "small", fullWidth: true },
                      }}
                    />
                  </Stack>
                ) : (
                  <TextField
                    label="תיאור זמן"
                    size="small"
                    value={draft.timeDescription}
                    onChange={(e) =>
                      setDraft((prev) =>
                        prev
                          ? {
                              ...prev,
                              timeDescription: e.target.value.slice(
                                0,
                                TIME_DESCRIPTION_MAX_LENGTH,
                              ),
                            }
                          : prev,
                      )
                    }
                    inputProps={{ maxLength: TIME_DESCRIPTION_MAX_LENGTH }}
                    fullWidth
                  />
                )}
              </Stack>
            </LocalizationProvider>
            </Stack>
            )}
            <FormControlLabel
              sx={{ m: 0, alignSelf: "flex-start" }}
              control={
                <Checkbox
                  size="small"
                  checked={apply.location}
                  onChange={(e) =>
                    setApply((prev) => ({ ...prev, location: e.target.checked }))
                  }
                />
              }
              label={
                <Typography variant="body2">
                  {ADMIN_GROUP_BULK_EDIT_APPLY_LOCATION}
                </Typography>
              }
            />
            {apply.location && (
              <>
                <FormControlLabel
                  sx={{ alignSelf: "flex-start", m: 0 }}
                  control={
                    <Checkbox
                      size="small"
                      checked={draft.isRemote}
                      onChange={(e) =>
                        setDraft((prev) =>
                          prev ? { ...prev, isRemote: e.target.checked } : prev,
                        )
                      }
                    />
                  }
                  label={
                    <Typography variant="body2">התנדבות מרחוק</Typography>
                  }
                />
                {!draft.isRemote && (
                  <FormGoogle
                    label="מיקום"
                    fullWidth
                    size="small"
                    placeValue={draft.location}
                    onLocationChange={({ locationName, latLng }) => {
                      setDraft((prev) =>
                        prev
                          ? {
                              ...prev,
                              location: locationName,
                              coordinates: latLng,
                            }
                          : prev,
                      );
                    }}
                  />
                )}
              </>
            )}
            <FormControlLabel
              sx={{ m: 0, alignSelf: "flex-start" }}
              control={
                <Checkbox
                  size="small"
                  checked={apply.lastCall}
                  onChange={(e) =>
                    setApply((prev) => ({ ...prev, lastCall: e.target.checked }))
                  }
                />
              }
              label={
                <Typography variant="body2">
                  {ADMIN_GROUP_BULK_EDIT_APPLY_LAST_CALL}
                </Typography>
              }
            />
            {apply.lastCall && (
              <LocalizationProvider
                dateAdapter={AdapterDayjs}
                localeText={
                  heIL.components.MuiLocalizationProvider.defaultProps.localeText
                }
                adapterLocale="he"
              >
                <DateTimePicker
                  label="מועד אחרון להרשמה"
                  format="DD/MM/YYYY HH:mm"
                  ampm={false}
                  value={
                    draft.lastCallToVolunteers
                      ? dayjs(draft.lastCallToVolunteers)
                      : null
                  }
                  onChange={(value: DateEvent) => {
                    if (!value || !isValid(value.toDate())) return;
                    const picked = new Date(value.$d ?? value.toDate());
                    setDraft((prev) =>
                      prev ? { ...prev, lastCallToVolunteers: picked } : prev,
                    );
                  }}
                  slotProps={{
                    textField: { size: "small", fullWidth: true },
                  }}
                />
              </LocalizationProvider>
            )}
            </Alert>
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {step === "preview" ? (
          <>
            <Button onClick={() => setStep("edit")} disabled={isSaving}>
              {ADMIN_GROUP_BULK_EDIT_BACK}
            </Button>
            <Button
              variant="contained"
              disabled={isSaving || !draft || overMax}
              onClick={() => void handleSave()}
            >
              {isSaving ? (
                <CircularProgress size={22} />
              ) : (
                ADMIN_GROUP_BULK_EDIT_CONFIRM
              )}
            </Button>
          </>
        ) : (
          <>
            <Button onClick={onClose} disabled={isSaving}>
              ביטול
            </Button>
            <Button
              variant="contained"
              disabled={isSaving || !draft || overMax || !anyApply(apply)}
              onClick={openPreview}
            >
              {ADMIN_GROUP_BULK_EDIT_PREVIEW}
            </Button>
          </>
        )}
      </DialogActions>
      {isAddAidRelated && (
        <AidRelatedCard
          isAdd={isAddAidRelated}
          defaultValues={undefined}
          onSave={async (association: Association) => {
            const asso = await associationRepo.findId(association.id, {
              include: { user: true },
            });
            setDraft((prev) =>
              prev
                ? {
                    ...prev,
                    associationRequested: asso ?? undefined,
                    associationRequestedId: asso?.id ?? null,
                  }
                : prev,
            );
          }}
          onClose={() => setIsAddAidRelated(false)}
        />
      )}
      <TeamsSelectDialog
        open={!!teamsAnchor}
        onClose={() => setTeamsAnchor(null)}
        returnFullTeam
        value={draft?.teams ?? []}
        onSave={(value) => {
          const teams = value as Team[];
          setDraft((prev) => (prev ? { ...prev, teams } : prev));
          setTeamsAnchor(null);
        }}
      />
    </Dialog>
  );
};
